import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// AuthCallback runs once when the URL contains `#session_id=<id>` (after Google OAuth).
// It exchanges the session_id for our app JWT on the backend, then redirects to /.
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-invocation
    if (processed.current) return;
    processed.current = true;

    const exchange = async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      // Always clear the fragment so a refresh doesn't reuse a spent session_id
      try { window.history.replaceState({}, document.title, window.location.pathname); } catch {}

      if (!m) {
        nav("/login", { replace: true });
        return;
      }
      const sessionId = decodeURIComponent(m[1]);
      try {
        const { data } = await api.post(
          "/auth/google/session",
          {},
          { headers: { "X-Session-ID": sessionId } }
        );
        if (data?.access_token) localStorage.setItem("access_token", data.access_token);
        await refresh();
        nav("/", { replace: true });
      } catch (e) {
        // Detail string from FastAPI → user-friendly message
        let msg = formatApiErrorDetail(e?.response?.data?.detail);
        if (!e?.response) {
          // Network error / CORS / DNS — no HTTP response was received
          msg = "Couldn't reach the server. Check your network connection and try again — if it keeps happening, ping IT.";
        } else if (!e?.response?.data?.detail) {
          msg = `Google sign-in failed (HTTP ${e.response.status}). Please try again or contact IT if it persists.`;
        }
        nav("/login", { replace: true, state: { googleError: msg } });
      }
    };

    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="text-center">
        <div className="font-display font-extrabold text-2xl tracking-tight text-ps-deep">Signing you in…</div>
        <div className="text-sm text-slate-500 mt-2">Verifying your Google session</div>
        <div className="mt-6 indus-bar mx-auto" style={{ width: "12rem" }} />
      </div>
    </div>
  );
}
